/**
 * MechaGuard - EffectSystem (瞬时特效系统)
 *
 * 管理寿命很短的表现层对象：激光束、连锁闪电、冰霜脉冲环、爆炸冲击波。
 * 它们不参与任何游戏逻辑判定，纯粹用于视觉反馈，因此与实体列表分开管理。
 *
 * 闪电的抖动在「创建时」一次性烘焙好，避免每帧重算随机数导致
 * 光线闪烁且浪费性能。
 */

export class EffectSystem {
  constructor() {
    /** @type {object[]} */
    this.effects = [];
  }

  get count() {
    return this.effects.length;
  }

  /* ===================================================
   * 创建
   * =================================================== */

  /** 直线光束（激光） */
  addBeam(x1, y1, x2, y2, color, life = 0.12) {
    this.effects.push({
      type: 'beam',
      x1, y1, x2, y2,
      color,
      life,
      maxLife: life,
    });
  }

  /**
   * 连锁闪电
   * @param {{x:number,y:number}[]} points 折线顶点
   */
  addChain(points, color, life = 0.18) {
    if (!points || points.length < 2) return;

    const segs = [];
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i];
      const b = points[i + 1];

      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.hypot(dx, dy) || 1;

      // 垂直单位向量，用于抖动偏移
      const nx = -dy / len;
      const ny = dx / len;

      const steps = 5;
      if (i === 0) segs.push({ x: a.x, y: a.y });

      for (let s = 1; s <= steps; s++) {
        const t = s / steps;
        // 终点必须精确落在目标上，否则闪电会「打偏」
        const jitter = s === steps ? 0 : (Math.random() - 0.5) * Math.min(22, len * 0.25);
        segs.push({
          x: a.x + dx * t + nx * jitter,
          y: a.y + dy * t + ny * jitter,
        });
      }
    }

    this.effects.push({ type: 'chain', points: segs, color, life, maxLife: life });
  }

  /** 扩散脉冲环（冰冻塔） */
  addRing(x, y, radius, color, life = 0.45) {
    this.effects.push({ type: 'ring', x, y, radius, color, life, maxLife: life });
  }

  /** 爆炸冲击波（等离子溅射） */
  addExplosion(x, y, radius, color, life = 0.3) {
    this.effects.push({ type: 'explosion', x, y, radius, color, life, maxLife: life });
  }

  /* ===================================================
   * 更新 / 绘制
   * =================================================== */

  update(dt) {
    const list = this.effects;
    let write = 0;

    for (let i = 0; i < list.length; i++) {
      const e = list[i];
      e.life -= dt;
      if (e.life <= 0) continue;
      list[write++] = e;
    }

    list.length = write;
  }

  draw(ctx) {
    const list = this.effects;
    if (list.length === 0) return;

    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    for (let i = 0; i < list.length; i++) {
      const e = list[i];
      const t = e.maxLife > 0 ? Math.max(0, e.life / e.maxLife) : 0;

      switch (e.type) {
        case 'beam':   this._drawBeam(ctx, e, t); break;
        case 'chain':  this._drawChain(ctx, e, t); break;
        case 'ring':   this._drawRing(ctx, e, t); break;
        case 'explosion': this._drawExplosion(ctx, e, t); break;
        default: break;
      }
    }

    ctx.restore();
  }

  clear() {
    this.effects.length = 0;
  }

  /* ===================================================
   * 各类型绘制
   * =================================================== */

  _drawBeam(ctx, e, t) {
    ctx.globalAlpha = 0.25 * t;
    ctx.strokeStyle = e.color;
    ctx.lineWidth = 7;
    ctx.beginPath();
    ctx.moveTo(e.x1, e.y1);
    ctx.lineTo(e.x2, e.y2);
    ctx.stroke();

    ctx.globalAlpha = t;
    ctx.lineWidth = 1.8;
    ctx.strokeStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(e.x1, e.y1);
    ctx.lineTo(e.x2, e.y2);
    ctx.stroke();
  }

  _drawChain(ctx, e, t) {
    const pts = e.points;

    const trace = () => {
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
    };

    ctx.globalAlpha = 0.3 * t;
    ctx.strokeStyle = e.color;
    ctx.lineWidth = 6;
    trace();
    ctx.stroke();

    ctx.globalAlpha = t;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.6;
    trace();
    ctx.stroke();
  }

  _drawRing(ctx, e, t) {
    const progress = 1 - t;                    // 0 -> 1
    const r = e.radius * (0.25 + progress * 0.8);

    ctx.globalAlpha = t * 0.9;
    ctx.strokeStyle = e.color;
    ctx.lineWidth = 3;

    ctx.beginPath();
    ctx.arc(e.x, e.y, r, 0, Math.PI * 2);
    ctx.stroke();

    // 内侧第二圈，制造层次
    ctx.globalAlpha = t * 0.4;
    ctx.beginPath();
    ctx.arc(e.x, e.y, r * 0.72, 0, Math.PI * 2);
    ctx.stroke();
  }

  _drawExplosion(ctx, e, t) {
    const progress = 1 - t;
    const r = e.radius * (0.5 + progress * 0.7);

    ctx.globalAlpha = t * 0.5;
    ctx.fillStyle = e.color;
    ctx.beginPath();
    ctx.arc(e.x, e.y, r, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = t * 0.95;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(e.x, e.y, r, 0, Math.PI * 2);
    ctx.stroke();
  }
}
